import {
  ChatMessage,
  ChatParticipant,
  OrganizationContact,
  OrganizationMember,
} from '@prisma/client'
import { Avatar } from 'antd'
import dayjs from 'dayjs'
import { CSSProperties, useMemo } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { DesignSystem } from '~/core/designSystem'
import { OrganizationType } from '~/features/organization/common'
import { OrganizationContactCommon } from '~/features/organizationContact/common'
import { OrganizationMemberClientShared } from '~/features/organizationMember/clientShared'
import { IntercomClient } from '~/plugins/intercom/client'
import { Toolbox } from '~/plugins/toolbox/shared'
import { Utility } from '~/plugins/toolbox/shared/utility'
import { RenderAgentDisabled } from '../components/RenderAgentDisabled'
import { RenderDateSplitter } from '../components/RenderDateSplitter'
import { RenderMessage } from '../components/RenderMessage'
import { RenderMessageAgent } from '../components/RenderMessageAgent'
import { RenderUsageLimitReached } from '../components/RenderUsageLimitReached'
import { BubbleMessage, BubbleRoles, UIChatItem, UIChatThread } from '../types'
import { useReactions } from './useReactions'

type Props = {
  organization: OrganizationType
  organizationMember: OrganizationMember
  organizationMembers: OrganizationMemberFromContext[]
  organizationContacts: OrganizationContact[]
  messages: ChatMessage[]
  participants: ChatParticipant[]
  onOpenThread?: (message: ChatMessage) => void
  onSendDraft?: (message: ChatMessage) => void
  onDelete?: (message: ChatMessage) => void
}

export const useBubbles = ({
  organization,
  organizationMember: organizationMemberCurrent,
  organizationMembers,
  organizationContacts,
  messages,
  participants,
  onOpenThread,
  onSendDraft,
  onDelete,
}: Props) => {
  const reactionHelper = useReactions({
    organizationMember: organizationMemberCurrent,
  })

  const { show } = IntercomClient.use()

  const onAskForHelp = () => {
    show()
  }

  const findMessageParticipant = (message: UIChatItem) => {
    const participant = participants.find(
      candidate => candidate.id === message.participantId,
    )

    const organizationMember = organizationMembers.find(
      candidate => candidate.id === participant?.organizationMemberId,
    )

    const organizationContact = organizationContacts.find(
      candidate => candidate.id === participant?.organizationContactId,
    )

    const pictureUrl =
      organizationContact?.pictureUrl ??
      OrganizationMemberClientShared.Helper.getPictureUrl(organizationMember)

    return {
      pictureUrl,
      participant,
      organizationMember,
      organizationContact,
    }
  }

  const findAgentAppSession = () => {
    if (!participants || !organizationMembers) {
      return
    }

    const organizationMembersParticipantIds = participants.map(
      participant => participant.organizationMemberId,
    )

    const organizationMembersParticipants = organizationMembers.filter(
      organizationMember =>
        organizationMembersParticipantIds.includes(organizationMember.id),
    )

    const organizationMemberAgent = organizationMembersParticipants.find(
      organizationMember => organizationMember.role === 'AGENT',
    )

    return organizationMemberAgent?.appSession
  }

  const appSession = findAgentAppSession()

  const items: BubbleMessage[] = useMemo(() => {
    const messagesBuilt: BubbleMessage[] = []

    messages.forEach((message, index) => {
      const item = message as any as UIChatItem

      const {
        pictureUrl,
        participant,
        organizationMember,
        organizationContact,
      } = findMessageParticipant(item)

      const isFromContact = !!organizationContact
      const isFromMe =
        organizationMember?.id === organizationMemberCurrent.id || isFromContact

      const thread: UIChatThread = {
        countMessages: item._count.children,
        message: item.children[0],
        pictureUrl: null,
      }

      if (thread.message) {
        const data = findMessageParticipant(thread.message)

        thread.pictureUrl = data.pictureUrl
      }

      const initials = Toolbox.Utility.stringToInitials(
        [
          organizationContact?.firstname,
          organizationContact?.lastname,
          organizationContact?.email,
          organizationMember?.user?.name,
        ]
          .filter(Boolean)
          .join(' '),
      )

      const avatarStyle: CSSProperties = organizationContact
        ? {
            background:
              OrganizationContactCommon.Helper.getColor(organizationContact),
          }
        : {}

      const messageBubble: BubbleMessage = {
        role: isFromMe ? 'user' : 'agent',
        variant: 'outlined',
        avatar: (
          <Avatar src={pictureUrl} size={32} style={avatarStyle}>
            {initials}
          </Avatar>
        ),
        shape: 'round',
        content: {
          item,
          participant,
          organizationMember,
          organizationContact,
          thread,
        },
      }

      messagesBuilt.push(messageBubble)

      const dateCurrent = dayjs(message.createdAt).startOf('day')
      const messageAfter = messages[index + 1]

      if (messageAfter) {
        const dateAfter = dayjs(messageAfter.createdAt).startOf('day')

        if (!dateCurrent.isSame(dateAfter, 'day')) {
          messagesBuilt.push({
            role: 'date-splitter',
            content: {
              item: {
                id: dateCurrent.toISOString(),
              },
              date: dateCurrent,
            },
          })
        }
      } else {
        messagesBuilt.push({
          role: 'date-splitter',
          content: {
            item: {
              id: dateCurrent.toISOString(),
            },
            date: dateCurrent,
          },
        })
      }
    })

    const isAgentDisabled = appSession?.status === 'DISABLED'

    if (isAgentDisabled) {
      messagesBuilt.unshift({
        role: 'agent-disabled',
        content: {
          item: {
            id: Utility.getUUID(),
          },
          agentName: appSession?.name,
          appSession,
        },
      })
    }

    const isLimitReached = organization.countTokens > organization.maxTokens

    if (isLimitReached && !isAgentDisabled) {
      messagesBuilt.unshift({
        role: 'agent-limit-reached',
        content: {
          item: {
            id: Utility.getUUID(),
          },
          agentName: appSession?.name,
          appSession,
        },
      })
    }

    return messagesBuilt
  }, [messages, messages?.length, participants])

  const roles: BubbleRoles = {
    ['date-splitter']: {
      placement: 'start',
      messageRender: (props: any) => <RenderDateSplitter {...props} />,
    },
    ['agent-disabled']: {
      placement: 'start',
      messageRender: (props: any) => <RenderAgentDisabled {...props} />,
    },
    ['agent-limit-reached']: {
      placement: 'start',
      messageRender: (props: any) => <RenderUsageLimitReached {...props} />,
    },
    agent: {
      placement: 'start',
      messageRender: (props: any) => (
        <RenderMessageAgent
          {...props}
          onOpenThread={
            onOpenThread ? () => onOpenThread?.(props.item) : undefined
          }
          onReaction={async reaction => {
            await reactionHelper.setReaction(props.item, reaction)
          }}
          onAskForHelp={onAskForHelp}
          onSend={onSendDraft}
          onDelete={onDelete ? () => onDelete(props.item) : undefined}
        />
      ),
      styles: {
        content: {
          position: 'relative',
          background: DesignSystem.ThemeConst.CONSUMER_LIGHTGREY,
        },
      },
    },
    user: {
      placement: 'end',
      messageRender: (props: any) => (
        <RenderMessage
          {...props}
          onOpenThread={
            onOpenThread ? () => onOpenThread?.(props.item) : undefined
          }
          onSend={onSendDraft}
          onDelete={onDelete ? () => onDelete(props.item) : undefined}
        />
      ),
      styles: {
        content: {
          position: 'relative',
        },
      },
    },
    // ['form-summary']: { // TODO
    //   placement: 'end',
    //   messageRender: (props: any) => (renderForm ? renderForm() : <></>),
    //   styles: {
    //     content: {
    //       position: 'relative',
    //       width: '100%',
    //     },
    //   },
    // },
  }

  return {
    items,
    roles,
  }
}
